import { createApplicationBuilder, createModuleBuilder } from '@evidentid/dashboard-commons/app';
import { createAuthModule } from '@evidentid/dashboard-commons/modules/auth';
import { createAuthConnectorModule } from '@evidentid/dashboard-commons/modules/auth-connector';
import { createAuthConnectorNotifySnackbarModule } from '@evidentid/dashboard-commons/modules/auth-connector-notify-snackbar';
import { createConfigApiClientModule } from '@evidentid/dashboard-commons/modules/config-api-client';
import { createIdleLogoutModule } from '@evidentid/dashboard-commons/modules/idle-logout';
import { createLoggerModule } from '@evidentid/dashboard-commons/modules/logger';
import { createPersistVueErrorsModule } from '@evidentid/dashboard-commons/modules/persist-vue-errors';
import { createPersistingErrorModule } from '@evidentid/dashboard-commons/modules/persisting-error';
import { createRbacModule } from '@evidentid/dashboard-commons/modules/role-base-access-control';
import { createRpWebApiClientModule } from '@evidentid/dashboard-commons/modules/rpweb-api-client';
import { createSnackbarModule } from '@evidentid/dashboard-commons/modules/snackbar';
import { createSupportModule } from '@evidentid/dashboard-commons/modules/support';
import { createTrackRoutesModule } from '@evidentid/dashboard-commons/modules/track-routes';
import { createTrackerModule } from '@evidentid/dashboard-commons/modules/tracker';
import TrackerUserData from '@evidentid/tracker/TrackerUserData';

import MainComponent from '@/components/Main.vue';
import { apiSettingsModule } from '@/modules/api-settings';
import { dashboardModule } from '@/modules/dashboard';
import { dashboardConfigurationModule } from '@/modules/dashboard-configuration';
import { decisioningCriteriaModule } from '@/modules/decisioning-criteria';
import { designExamplesModule } from '@/modules/design-examples';
import { documentDataReviewModule } from '@/modules/document-data-review';
import { entityActionsReviewModule } from '@/modules/entity-actions-review';
import { entityDetailsModule } from '@/modules/entity-details';
import { entityDocumentsModule } from '@/modules/entity-documents';
import { entityEventsModule } from '@/modules/entity-events';
import { entityFilteringModule } from '@/modules/entity-filtering';
import { entityManagementModule } from '@/modules/entity-management';
import { entityNotesModule } from '@/modules/entity-notes';
import { entityRequestModule } from '@/modules/entity-request';
import { evieAiModule } from '@/modules/evie-ai';
import { createGatewayApiClientModule } from '@/modules/gateway-api-client';
import { globalEventsModule } from '@/modules/global-events';
import { goAppSettingsModule } from '@/modules/go-app-settings';
import { kbModule } from '@/modules/kb';
import { createNotificationsConfigurationModule } from '@/modules/notifications-configuration';
import { productUpdatesModule } from '@/modules/product-updates';
import { ssoProvisioningModule } from '@/modules/sso-provisioning';
import { createUserManagementModule } from '@/modules/user-management';

const base = createModuleBuilder()
    .registerRoutes(() => [
        {
            path: '/',
            redirect: () => ({ name: 'dashboard' }),
        },
        {
            // For now there exists no home page.
            // PRODUCT-26110 Phase 2B2: vue-router 4 removed the `path: '*'` catch-all
            // syntax entirely (throws a matcher error at router creation) — it must be
            // a named param with a custom regex: `/:pathMatch(.*)*`.
            path: '/:pathMatch(.*)*',
            name: '404',
            redirect: () => ({ name: 'error', params: { reason: 'page-not-found' }, query: {} }),
        },
    ])
    .end();

export const createInsuranceApplication = createApplicationBuilder(MainComponent)
    .use(createTrackRoutesModule())
    .use(createSnackbarModule())
    .use(createPersistingErrorModule())
    .use(createPersistVueErrorsModule())
    .use(createAuthModule())
    .use(createAuthConnectorModule())
    .use(createAuthConnectorNotifySnackbarModule())
    .use(createRpWebApiClientModule())
    .use(createLoggerModule())
    .use(createTrackerModule<TrackerUserData & { currentRp?: string | null, rpsCount?: number }>())
    .use(createIdleLogoutModule())
    .use(createRbacModule())
    .use(createUserManagementModule())
    .use(createConfigApiClientModule())
    .use(createGatewayApiClientModule())
    .use(createSupportModule())
    .use(productUpdatesModule)
    .use(kbModule)
    .use(evieAiModule)
    .use(dashboardModule)
    .use(dashboardConfigurationModule)
    .use(decisioningCriteriaModule)
    .use(entityManagementModule)
    .use(entityFilteringModule)
    .use(entityDocumentsModule)
    .use(entityRequestModule)
    .use(entityDetailsModule)
    .use(entityEventsModule)
    .use(globalEventsModule)
    .use(entityNotesModule)
    .use(entityActionsReviewModule)
    .use(apiSettingsModule)
    .use(goAppSettingsModule)
    .use(ssoProvisioningModule)
    .use(createNotificationsConfigurationModule())
    .use(documentDataReviewModule)
    .use(designExamplesModule)
    .use(base)
    .createFactory();
